import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useCallOrderSetIsModifyingField,
  useCallOrderTileId,
  useValidateCallOrder,
} from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectCallOrderErrors, selectExpiryCallOrder } from '@/store/orders/call/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export const CallOrderExpiry = () => {
  const tileId = useCallOrderTileId();
  const setIsModifyingField = useCallOrderSetIsModifyingField();
  const track = useTrackEvent();

  const { formatMessage, formatTime, locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const expiry = useAppSelector((state) => selectExpiryCallOrder(state, tileId));
  const errors = useAppSelector((state) => selectCallOrderErrors(state, tileId));

  const validateCallOrder = useValidateCallOrder();

  const handleIsGoodTillCancelledChange = (isGoodTillConcelled: boolean) => {
    track('Order action', 'Good till cancelled changed');
    validateCallOrder({
      expiry: isGoodTillConcelled
        ? { type: 'GoodTillCancelled' }
        : { type: 'ExpiryDate', date: undefined, time: undefined },
    });
  };

  const handleDateChange = (date: string) => {
    track('Order action', 'Expiry date changed');
    if (isNotDefined(expiry) || expiry.type === 'GoodTillCancelled') {
      return;
    }

    const newExpiry = {
      type: 'ExpiryDate' as const,
      date,
      time: expiry.time, // Keep the existing time if it exists
    };

    validateCallOrder({
      expiry: newExpiry,
    });
  };

  const expiryDateError = errors?.expiryDay;
  const expiryTimeError = errors?.expiryTime;

  const handleTimeChange = useCallback(
    ({
      hour = 0,
      minute = 0,
      second = 0,
      period,
    }: {
      hour?: number;
      minute?: number;
      second?: number;
      period?: 'AM' | 'PM';
    }) => {
      track('Order action', 'Expiry time changed');
      if (isNotDefined(expiry) || expiry.type === 'GoodTillCancelled') {
        return;
      }

      const hour24 = period === 'PM' && hour < 12 ? hour + 12 : hour;

      const newExpiry = {
        type: 'ExpiryDate' as const,
        date: expiry.date, // Keep the existing date from ref
        time: formatTime(new Date(0, 0, 0, hour24, minute, second), { timeStyle: 'medium', hour12: false }),
      };

      validateCallOrder({
        expiry: newExpiry,
      });
    },
    [expiry, validateCallOrder, formatTime],
  );

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  return (
    <OrderRow labelId="order.fields.expiry.label" alignTop>
      <Checkbox
        dataE2e="GTC-checkbox"
        checked={expiry?.type === 'GoodTillCancelled'}
        onCheckBoxChange={handleIsGoodTillCancelledChange}
        label={formatMessage({ id: 'order.fields.expiry.goodTillCanceled' })}
        size="sm"
      />

      {expiry?.type === 'ExpiryDate' && (
        <div
          className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
          data-e2e="expiry-date"
        >
          <OmsFieldErrorWrapper errors={expiryDateError}>
            <SgDatePicker
              size="sm"
              yearControls
              value={expiry.date}
              locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
              dateInputCultureInfo={getComputedLocale()}
              placeholder="Expiry date"
              onChange={handleDateChange}
              onFocus={() => setIsModifyingField(true)}
              onBlurInput={() => setIsModifyingField(false)}
              showClearButton={false}
              validationState={isDefined(expiryDateError) ? 'invalid' : undefined}
              dateFormat={DATE_FORMAT_TEXT}
            />
          </OmsFieldErrorWrapper>

          <OmsFieldErrorWrapper errors={expiryTimeError}>
            <SgTimepicker
              size="sm"
              appendTo="body"
              withSeconds
              value={isDefined(expiry.time) ? expiry.time : undefined}
              onTimeSelected={(e) => handleTimeChange(e.detail)}
              state={isDefined(expiryTimeError) ? 'invalid' : undefined}
            />
          </OmsFieldErrorWrapper>
        </div>
      )}
    </OrderRow>
  );
};
